import React, { useRef, type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	CloseButton,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Stack } from '@atlaskit/primitives/compiled';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

export type NotifyLargeAudienceWarningDialogProps = {
	isSaving: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	onCloseComplete: () => void;
};

const i18n = defineMessages({
	heading: {
		id: 'share-and-restrict-dialog.many-users-notify-warning.heading',
		defaultMessage: 'Notify everyone?',
		description: 'Heading for a dialog that asks if notifying many users is intended',
	},
	body: {
		id: 'share-and-restrict-dialog.many-users-notify-warning.body',
		defaultMessage:
			'You are about to share to a lot of individuals. It might be a little noisy to notify them all. Are you sure you want to notify everyone?',
		description: 'Body text for dialog that confirms notifying many users',
	},
	confirm: {
		id: 'share-and-restrict-dialog.many-users-notify-warning.confirm',
		defaultMessage: 'Notify everyone',
		description: 'Button label to confirm notifying many users',
	},
	cancel: {
		id: 'share-and-restrict-dialog.many-users-notify-warning.cancel',
		defaultMessage: 'Cancel',
		description: 'Button label to cancel notifying many users',
	},
});

export const NotifyLargeAudienceWarningDialog: FC<NotifyLargeAudienceWarningDialogProps> = ({
	isSaving,
	onConfirm,
	onCancel,
	onCloseComplete,
}) => {
	const focusRef = useRef<HTMLButtonElement>(null);
	const { Modal } = useChoreographerAssets();

	const closeModal = () => !isSaving && onCancel();

	return (
		<Modal
			width="medium"
			onCloseComplete={onCloseComplete}
			testId="modal-dialog-share-many-users-notify-warning"
		>
			<ModalHeader>
				<ModalTitle>
					<FormattedMessage {...i18n.heading} />
				</ModalTitle>
				<CloseButton onClick={closeModal} />
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<FormattedMessage {...i18n.body} />
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Button isDisabled={isSaving} onClick={onCancel}>
					<FormattedMessage {...i18n.cancel} />
				</Button>
				<Button appearance="primary" isDisabled={isSaving} onClick={onConfirm} ref={focusRef}>
					<FormattedMessage {...i18n.confirm} />
				</Button>
			</ModalFooter>
		</Modal>
	);
};
